import { create } from 'zustand'

type CaptureStatus = 'idle' | 'exporting' | 'ready'

export type ReportCapture = () => Promise<{ data: string | null; pdf: string | null }>

interface ActiveReportContextState {
    resource: string | null
    params: Record<string, string[]>
    data: string | null
    pdf: string | null
    /** Whether the viewer is currently exporting the CSV/PDF over JS9 for the AI assistant. */
    captureStatus: CaptureStatus
    /** Exports the currently rendered report run; registered by the viewer, run lazily on first chat send. */
    captureFn: ReportCapture | null
    capturePromise: Promise<void> | null
    /** Bumped on every registerCapture()/clearActiveReportContext(); an in-flight export only writes state while its run id is still current. */
    captureRunId: number
    /** Whether the backend has told us (success or failure) whether this resource's snapshot saved to S3. */
    snapshotResolved: boolean
    /**
     * Whether the "report context could not be uploaded" toast has already been shown once
     * this session. Not reset on report navigation — a bucket misconfiguration is systemic,
     * so we only nag the user about it once rather than on every report they open.
     */
    snapshotUploadErrorShown: boolean
    setActiveReportContext: (resource: string, params: Record<string, string[]>) => void
    setActiveReportData: (data: string | null) => void
    setActiveReportPdf: (pdf: string | null) => void
    /** Registers the capture for a new report run, discarding data captured for the previous one. */
    registerCapture: (captureFn: ReportCapture | null) => void
    /** Runs the registered capture once per report run; concurrent callers share the in-flight export. */
    ensureCaptured: () => Promise<void>
    setSnapshotResolved: (resolved: boolean) => void
    markSnapshotUploadErrorShown: () => void
    clearActiveReportContext: () => void
}

/**
 * Tracks the report currently open in the JasperReports viewer (render-view.jsx),
 * so the chat widget can tell the AI assistant what's on screen without the
 * user having to describe it. `data` is the report's CSV and `pdf` its base64-encoded
 * PDF export, both fetched by the viewer over its own authenticated JSR session — the
 * backend has no working credential to fetch these itself, so they ride along with
 * the chat request.
 */
export const useActiveReportContext = create<ActiveReportContextState>()(((set, get) => ({
    resource: null,
    params: {},
    data: null,
    pdf: null,
    captureStatus: 'idle',
    captureFn: null,
    capturePromise: null,
    captureRunId: 0,
    snapshotResolved: false,
    snapshotUploadErrorShown: false,
    setActiveReportContext: (resource, params) => set({ resource, params, snapshotResolved: false }),
    setActiveReportData: (data) => set({ data }),
    setActiveReportPdf: (pdf) => set({ pdf }),
    registerCapture: (captureFn) =>
        set((state) => ({
            captureFn,
            capturePromise: null,
            data: null,
            pdf: null,
            captureStatus: 'idle',
            captureRunId: state.captureRunId + 1,
        })),
    ensureCaptured: async () => {
        const { captureFn, capturePromise, captureStatus, captureRunId } = get()
        if (!captureFn || captureStatus === 'ready') return
        if (capturePromise) {
            await capturePromise
            return
        }
        // A re-run may register a new capture while this one is in flight; never let a stale
        // run overwrite the newer run's status, promise or data.
        const isCurrent = () => get().captureRunId === captureRunId
        const promise = (async () => {
            if (isCurrent()) set({ captureStatus: 'exporting' })
            let captured = false
            try {
                const { data, pdf } = await captureFn()
                captured = !!data
                if (isCurrent()) set({ data, pdf })
            } finally {
                // A failed export leaves the status idle so the next message retries it, instead of
                // every later question about this run going out without the report's data.
                if (isCurrent()) set({ captureStatus: captured ? 'ready' : 'idle', capturePromise: null })
            }
        })()
        if (isCurrent()) set({ capturePromise: promise })
        await promise
    },
    setSnapshotResolved: (snapshotResolved) => set({ snapshotResolved }),
    markSnapshotUploadErrorShown: () => set({ snapshotUploadErrorShown: true }),
    clearActiveReportContext: () =>
        set((state) => ({
            resource: null,
            params: {},
            data: null,
            pdf: null,
            captureStatus: 'idle',
            captureFn: null,
            capturePromise: null,
            captureRunId: state.captureRunId + 1,
            snapshotResolved: false,
        })),
})))

export default useActiveReportContext
